import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import authApi from '../api/auth.api';
import { User } from '../types/campaign.types';

interface AuthContextValue {
    user: User | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(() => {
        const stored = localStorage.getItem('authUser');
        return stored ? JSON.parse(stored) : null;
    });
    const [loading, setLoading] = useState(Boolean(localStorage.getItem('authToken')));

    const refreshUser = useCallback(async () => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const me = await authApi.me();
            setUser(me);
            localStorage.setItem('authUser', JSON.stringify(me));
        } catch {
            localStorage.removeItem('authToken');
            localStorage.removeItem('authUser');
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshUser();
    }, [refreshUser]);

    const login = async (email: string, password: string) => {
        const response = await authApi.login(email, password);
        localStorage.setItem('authToken', response.access_token);
        localStorage.setItem('authUser', JSON.stringify(response.user));
        setUser(response.user);
    };

    const logout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        setUser(null);
        window.location.href = '/login';
    };

    const value = useMemo(() => ({ user, loading, login, logout, refreshUser }), [user, loading, refreshUser]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
